import {
  idealTempRanges,
  moonScoreMapping,
  troutMoonScoreAdjustment,
} from "./fishSpecies";

const MAX_SCORE = 155;

// Pure function: takes known conditions and returns a clamped 0-100 chance
// of a bite. No network, no geolocation, no dates read internally - makes
// this the one function that's actually unit-testable end to end.
export function calculateForecastScore(fishType, conditions) {
  const {
    weather,
    rain_intensity,
    barometric_pressure,
    pressure_trend,
    temperature,
    wind_speed,
    time_of_day,
    month,
    moon_phase,
  } = conditions;

  let score = 0;

  // Weather conditions. Note: OpenWeather reports "Clear", never "sunny".
  switch (fishType) {
    case "cod":
    case "haddock":
      // Cod and Haddock might prefer overcast conditions, which could lead to better fishing.
      if (weather === "clouds") {
        score += 25;
      } else if (weather === "rain" && rain_intensity === "light") {
        score += 20;
      } else if (weather === "rain" && rain_intensity === "moderate") {
        score += 10;
      } else if (weather === "rain" && rain_intensity === "heavy") {
        score -= 5; // Heavy rain disturbs the water and reduces visibility.
      } else if (weather === "clear") {
        score -= 5;
      }
      break;

    case "mackerel":
      // Mackerel may not have a strong preference, but slight overcast or cloudy conditions could be beneficial.
      if (weather === "clouds") {
        score += 20;
      } else if (weather === "rain" && rain_intensity === "light") {
        score += 15;
      } else if (weather === "rain" && rain_intensity === "moderate") {
        score += 8;
      } else if (weather === "rain" && rain_intensity === "heavy") {
        score -= 8;
      } else if (weather === "clear") {
        score -= 10;
      }
      break;

    case "bass":
      // Bass fishing can be good on overcast days, and some anglers believe that bass bite more just before a storm.
      if (
        weather === "clouds" ||
        (weather === "rain" && rain_intensity === "light")
      ) {
        score += 30;
      } else if (weather === "rain" && rain_intensity === "moderate") {
        score += 15;
      } else if (weather === "rain" && rain_intensity === "heavy") {
        score -= 10;
      } else if (weather === "clear") {
        score -= 10;
      }
      break;

    case "trout":
      // Trout may prefer stable weather conditions, but slight cloud cover can also be beneficial.
      if (weather === "clouds") {
        score += 20;
      } else if (weather === "rain" && rain_intensity === "light") {
        score += 25;
      } else if (weather === "rain" && rain_intensity === "moderate") {
        score += 12;
      } else if (weather === "rain" && rain_intensity === "heavy") {
        score -= 10;
      } else if (weather === "clear") {
        score += 5; // Clear weather isn't as detrimental for trout compared to some other species.
      }
      break;
    default:
      score += 0;
  }

  // Wind Speed
  if (wind_speed < 2) {
    score -= 10; // Very light wind might lead to lower fish activity.
  } else if (wind_speed >= 2 && wind_speed <= 5) {
    score += 15; // Ideal conditions for stimulating fish activity.
  } else if (wind_speed > 5 && wind_speed <= 8) {
    score += 5; // Strong winds might still promote some fish activity, especially in deeper waters.
  } else if (wind_speed > 8) {
    score -= 5; // Very strong winds might reduce surface activity and push fish to deeper areas.
  }

  // Time of day
  switch (fishType) {
    case "cod":
    case "haddock":
      // Cod and Haddock are often more active during early morning and late afternoon.
      if (
        time_of_day === "early_morning" ||
        time_of_day === "late_afternoon"
      ) {
        score += 25;
      } else if (
        time_of_day === "mid_morning" ||
        time_of_day === "early_afternoon"
      ) {
        score += 15;
      }
      break;

    case "mackerel":
      // Mackerel can be caught throughout the day, but dawn and dusk might be slightly more productive.
      if (
        time_of_day === "early_morning" ||
        time_of_day === "late_afternoon"
      ) {
        score += 20;
      } else if (
        time_of_day === "mid_morning" ||
        time_of_day === "early_afternoon"
      ) {
        score += 10;
      }
      break;

    case "bass":
      // Bass are known to bite more during early morning and late afternoon.
      if (
        time_of_day === "early_morning" ||
        time_of_day === "late_afternoon"
      ) {
        score += 30;
      } else if (
        time_of_day === "mid_morning" ||
        time_of_day === "early_afternoon"
      ) {
        score += 10;
      }
      break;

    case "trout":
      // Trout fishing can be good in the early morning and late afternoon, especially for fly-fishing.
      if (
        time_of_day === "early_morning" ||
        time_of_day === "late_afternoon"
      ) {
        score += 25;
      } else if (
        time_of_day === "mid_morning" ||
        time_of_day === "early_afternoon"
      ) {
        score += 15;
      }
      break;
    default:
      score += 0;
  }

  // Month
  if (fishType === "cod" && (month >= 10 || month <= 3)) {
    score += 15;
  } else if (fishType === "haddock" && (month >= 11 || month <= 3)) {
    score += 15;
  } else if (fishType === "mackerel" && month >= 5 && month <= 9) {
    score += 15;
  } else if (fishType === "bass" && month >= 5 && month <= 10) {
    score += 15;
  } else if (fishType === "trout" && month >= 3 && month <= 10) {
    score += 15;
  } else {
    score -= 25;
  }

  // Moon phase
  switch (fishType) {
    case "cod":
    case "haddock":
    case "mackerel":
    case "bass":
      score += moonScoreMapping[moon_phase] || 0;
      break;
    case "trout": {
      const troutScore = moonScoreMapping[moon_phase]
        ? moonScoreMapping[moon_phase] +
          (troutMoonScoreAdjustment[moon_phase] || 0)
        : 0;
      score += troutScore;
      break;
    }
    default:
      // No additional score for unlisted fish types
      break;
  }

  // Temperature
  const tempRange = idealTempRanges[fishType] || { min: 0, max: 30 }; // Default range if fishType is not listed
  if (temperature >= tempRange.min && temperature <= tempRange.max) {
    score += 10; // Within ideal range
  } else if (
    (temperature >= tempRange.min - 3 && temperature < tempRange.min) ||
    (temperature > tempRange.max && temperature <= tempRange.max + 3)
  ) {
    score += 5; // Slightly outside ideal range
  } else {
    score -= 5; // Far outside ideal range
  }

  // Barometric pressure - absolute level
  if (barometric_pressure < 1000 || barometric_pressure > 1030) {
    score -= 10; // Deep low or strong high pressure generally suppresses feeding activity.
  } else if (barometric_pressure >= 1008 && barometric_pressure <= 1020) {
    score += 15; // Stable, near-average pressure band.
  } else {
    score += 5; // Moderately away from average but not extreme.
  }

  // Barometric pressure - trend (based on the next forecast step)
  if (pressure_trend === "falling") {
    score += 10; // Fish commonly feed more actively as pressure drops ahead of a front.
  } else if (pressure_trend === "rising") {
    score -= 5; // Rising pressure after a front often slows feeding.
  }

  return Math.max(0, Math.min(100, (score / MAX_SCORE) * 100));
}
